import { useEffect, useState } from "react";
import api from "../lib/api";
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const ymLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS[+m - 1]} ${y.slice(2)}`; };
const CAL = "#16a34a";
const DES = "#dc2626";

const Slope = ({ label, v, color, goodUp, testId }) => {
  const up = v >= 0;
  const good = up === goodUp;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold" data-testid={testId}>
      <span className="w-2 h-2 rounded-full" style={{ background: color }}/>
      {label}
      <span className="inline-flex items-center gap-1" style={{ color: good ? "#059669" : "#dc2626" }}>
        <Icon size={13}/>{up ? "+" : ""}{v} pp/mes
      </span>
    </div>
  );
};

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold shadow-xl">
      <div className="font-extrabold mb-1">{ymLabel(p.year_month)} · {p.total.toLocaleString("es-DO")} leads</div>
      <div style={{ color: "#86efac" }}>Calificados: {p.calif_pct}% ({p.calificados.toLocaleString("es-DO")})</div>
      <div style={{ color: "#fca5a5" }}>Descalificados: {p.descal_pct}% ({p.descalificados.toLocaleString("es-DO")})</div>
    </div>
  );
};

export default function QualificationTrendChart({ filters }) {
  const [data, setData] = useState(null);
  const { year, brand, model, showroom, business_unit, channel } = filters;
  useEffect(() => {
    setData(null);
    api.get("/sales-performance/qualification-trend", { params: { year, brand, model, showroom, business_unit, channel } })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [year, brand, model, showroom, business_unit, channel]);

  const rows = (data?.rows || []).map((r) => ({ ...r, label: ymLabel(r.year_month) }));
  return (
    <section className="surface p-5 sm:p-6 mb-6" data-testid="sp-qualification-trend">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-xl font-extrabold font-display">Tendencia de Calificación y Descalificación</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            % del total de leads de cada mes · línea punteada = tendencia lineal. Calificados = histórico (alguna vez calificado).
          </p>
        </div>
        {data?.rows?.length > 1 && (
          <div className="flex flex-wrap gap-2">
            <Slope label="Calificación" v={data.calif_slope} color={CAL} goodUp testId="sp-qt-calif-slope"/>
            <Slope label="Descalificación" v={data.descal_slope} color={DES} goodUp={false} testId="sp-qt-descal-slope"/>
          </div>
        )}
      </div>
      {!data ? (
        <div className="h-64 rounded-lg bg-gray-50 animate-pulse" data-testid="sp-qt-loading"/>
      ) : !rows.length ? (
        <div className="text-sm text-gray-500 py-10 text-center" data-testid="sp-qt-empty">Sin leads para estos filtros.</div>
      ) : (
        <div style={{ height: 300 }} data-testid="sp-qt-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 20, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false}/>
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fontWeight: 700, fill: "#6b7280" }}/>
              <YAxis tickLine={false} axisLine={false} unit="%" tick={{ fontSize: 11, fill: "#9ca3af" }} domain={[0, "auto"]}/>
              <Tooltip content={<Tip/>}/>
              <Legend iconType="plainline" verticalAlign="top" wrapperStyle={{ fontSize: 11, fontWeight: 700, paddingBottom: 12 }}/>
              <Line name="% Calificados" dataKey="calif_pct" stroke={CAL} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }}>
                <LabelList dataKey="calif_pct" position="top" style={{ fontSize: 10, fontWeight: 800, fill: CAL }} formatter={(v) => `${v}%`}/>
              </Line>
              <Line name="% Descalificados" dataKey="descal_pct" stroke={DES} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }}>
                <LabelList dataKey="descal_pct" position="top" style={{ fontSize: 10, fontWeight: 800, fill: DES }} formatter={(v) => `${v}%`}/>
              </Line>
              <Line name="Tendencia calificados" dataKey="calif_trend" stroke={CAL} strokeDasharray="6 4" strokeWidth={1.5} dot={false} activeDot={false} strokeOpacity={0.7}/>
              <Line name="Tendencia descalificados" dataKey="descal_trend" stroke={DES} strokeDasharray="6 4" strokeWidth={1.5} dot={false} activeDot={false} strokeOpacity={0.7}/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
